<script setup lang="ts">
import { computed, ref, shallowRef, watch, type Ref } from "vue";
import DimensionValuesPutDTO = App.Data.DimensionValuesPutDTO;
import { useI18n } from "vue-i18n";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import SelectPaletteDialog from "@/ui/components/colorPicker/SelectPaletteDialog.vue";
import { getColorsForPalette } from "@/ui/components/colorPicker/colorDefinitions";
import ClampedNumberInput from "@/modules/dimension_values/components/ClampedNumberInput.vue";
import Switch from "@/ui/components/switch/switch.vue";
import isEqual from "lodash/isEqual";
import { toRawDeep } from "@/Utils/to-raw-deep";
import { onBeforeRouteLeave } from "vue-router";
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";
import { useDragAndDrop } from "@/ui/composables/useDragAndDrop";

const loading = defineModel<boolean>("loading");
const values = defineModel<DimensionValuesPutDTO[] | null>("values");
const useDefaultSorting = defineModel<boolean>("useDefaultSorting");
const emit = defineEmits(["save", "delete"]);

const { dragstartHandle, dragoverHandle, dragleaveHandle, dragdropHandle } =
	useDragAndDrop(values as Ref<DimensionValuesPutDTO[]>);

const indexOf = (item: DimensionValuesPutDTO) =>
	values.value.findIndex(
		(value) => value.dimension_value_id === item.dimension_value_id
	);

const dropHandle = (ev: DragEvent, item: DimensionValuesPutDTO) => {
	if (useDefaultSorting.value) return;
	dragdropHandle(ev, indexOf(item));
	values.value.forEach((value, index) => (value.sort_order = index));
};

const savedValues = ref<DimensionValuesPutDTO[] | null>(
	structuredClone(toRawDeep(values.value))
);
const savedUseDefaultSorting = ref(useDefaultSorting.value);
const hasChanges = computed(
	() =>
		!isEqual(values.value, savedValues.value) ||
		useDefaultSorting.value !== savedUseDefaultSorting.value
);

watch(useDefaultSorting, (isDefault) => {
	if (isDefault) {
		values.value.forEach((value) => (value.sort_order = 0));
	}
});

const useSelectedFirstColor = ref<null | string>(null);
const userSelectedFirstColorFromPaletteUuid = ref<null | string>(null);

const { t } = useI18n();
const totalItems = shallowRef(10);
const itemsPerPage = shallowRef(10);
const headers = ref([
	{
		title: t("dimension_values.table.order"),
		key: "sort_order",
		align: "start",
		sortable: true,
	},
	{
		title: t("dimension_values.table.header"),
		key: "label",
		align: "start",
		sortable: true,
	},
	{
		title: t("dimension_values.table.color"),
		key: "color",
		align: "end",
		sortable: false,
	},
]);

const search = ref("");
const showPaletteDialog = ref(false);

const defineUserSelectedOptions = (paletteUuid: string, hexColor: string) => {
	userSelectedFirstColorFromPaletteUuid.value = paletteUuid;
	useSelectedFirstColor.value = hexColor;
};

const resetUserSelectedOptions = () => {
	userSelectedFirstColorFromPaletteUuid.value = null;
	useSelectedFirstColor.value = null;
};

const randomizeColors = async (uuid: string, startHexColor?: string) => {
	const colors = await getColorsForPalette(uuid);
	if (!colors.length) return;
	const foundIndex = startHexColor ? colors.indexOf(startHexColor) : -1;
	let colorIndex =
		foundIndex !== -1 ? foundIndex : Math.floor(Math.random() * colors.length);
	values.value.forEach((dimension) => {
		dimension.hex_color_value = colors[colorIndex];
		colorIndex = (colorIndex + 1) % colors.length;
	});

	resetUserSelectedOptions();
};

const save = () => {
	savedValues.value = structuredClone(toRawDeep(values.value));
	savedUseDefaultSorting.value = useDefaultSorting.value;
	emit("save");
};

const sortAlphabetically = () => {
	values.value.sort((a, b) => a.label.localeCompare(b.label));
	values.value.forEach((value, index) => (value.sort_order = index));
};

onBeforeRouteLeave((to, from, next) => {
	unsavedChangesDialog("dimension", hasChanges.value, next);
});
</script>

<template>
	<select-palette-dialog
		v-if="showPaletteDialog"
		v-model="showPaletteDialog"
		:initial-palette-uuid="userSelectedFirstColorFromPaletteUuid"
		:initial-hex-color="useSelectedFirstColor"
		@user-selects-palette="
			(uuid, startHexColor) => randomizeColors(uuid, startHexColor)
		"
	/>

	<div class="action-bar">
		<v-text-field
			v-model="search"
			variant="filled"
			placeholder="Search"
			prepend-inner-icon="mdi-magnify"
			hide-details
		/>
		<v-btn
			:disabled="loading"
			height="100%"
			elevation="0"
			variant="flat"
			color="secondary"
			prepend-icon="fa: fa-solid fa-fill-drip"
			@click="showPaletteDialog = true"
		>
			Randomize colors
		</v-btn>
		<Switch
			v-model="useDefaultSorting"
			v-model:loading="loading"
			:label="t('dimension_values.buttons.use_default_sorting')"
		/>
		<v-btn
			:disabled="loading || useDefaultSorting"
			height="100%"
			elevation="0"
			variant="flat"
			color="secondary"
			prepend-icon="fa: fa-solid fa-arrow-down-a-z"
			@click="sortAlphabetically"
		>
			{{ t("dimension_values.buttons.sort_alphabetically") }}
		</v-btn>
		<div class="h-100">
			<v-badge
				:model-value="hasChanges"
				color="error"
				offset-x="4"
				offset-y="4"
				dot
			>
				<v-btn
					:disabled="loading"
					rounded="rounded"
					elevation="0"
					icon="mdi-content-save"
					@click="save"
				/>
			</v-badge>
		</div>
	</div>

	<v-data-table
		v-model:items-per-page="itemsPerPage"
		:sort-by="[
			{ key: 'sort_order', order: 'asc' },
			{ key: 'label', order: 'asc' },
		]"
		density="comfortable"
		class="mt-2"
		:headers="headers as any"
		:items="values"
		:items-length="totalItems"
		:loading="loading"
		:search="search"
		item-value="name"
	>
		<template v-slot:loading>
			<v-skeleton-loader type="table-row@4"></v-skeleton-loader>
		</template>
		<template v-slot:item="{ item, index }">
			<tr
				:draggable="!useDefaultSorting"
				class="sortable-row"
				:class="{ locked: useDefaultSorting }"
				@dragstart="(ev) => dragstartHandle(ev, indexOf(item))"
				@dragover="(ev) => dragoverHandle(ev, indexOf(item))"
				@dragleave="dragleaveHandle"
				@drop="(ev) => dropHandle(ev, item)"
			>
				<td>
					<div class="d-flex align-center ga-2">
						<v-icon
							v-tooltip="
								useDefaultSorting
									? t('dimension_values.tooltips.order_locked')
									: null
							"
							class="drag-handle"
							size="small"
							:icon="useDefaultSorting ? 'mdi-lock' : 'mdi-drag-vertical'"
						/>
						<clamped-number-input
							v-model="item.sort_order"
							:min="0"
							:max="9999"
							density="ultra-compact"
							:disabled="useDefaultSorting"
						/>
					</div>
				</td>
				<td>
					{{ item.label }}
				</td>
				<td style="text-align: right; max-width: 30px">
					<color-picker
						v-model="item.hex_color_value"
						:on-color-click="
							index === 0 ? defineUserSelectedOptions : resetUserSelectedOptions
						"
						:show-locked-onto-for-randomize="
							useSelectedFirstColor === item.hex_color_value && index === 0
						"
					/>
				</td>
			</tr>
		</template>
	</v-data-table>
</template>

<style scoped>
.action-bar {
	display: grid;
	gap: 1rem;
	grid-template-columns: 1fr auto auto auto auto;
	align-items: center;

	@media only screen and (max-width: 480px) {
		grid-template-columns: 1fr;
		grid-template-rows: repeat(5, 1fr);
	}
}

input[type="number"] {
	display: flex;
	align-items: center;
	border-radius: 8px;
	height: 2rem;
	text-align: center;
}

.sortable-row {
	cursor: grab;
}

.sortable-row.locked {
	cursor: default;
}

.sortable-row.over {
	outline: 2px dashed rgb(var(--color-primary));
	outline-offset: -2px;
}

.drag-handle {
	color: rgb(var(--color-secondary-tonal));
}

.sortable-row.locked .drag-handle {
	opacity: 0.4;
}

input[type="color"] {
	appearance: none;
	height: 1.25rem;
	width: 2rem;
	background-color: transparent;

	&::-webkit-color-swatch-wrapper {
		padding: 0;
	}

	&::-webkit-color-swatch {
		border: 1px solid;
		border-radius: 4px;
	}
}
</style>
